import { AfterViewInit, Component, computed, ElementRef, EventEmitter, Input, linkedSignal, OnChanges, OnInit, Output, signal, Signal, SimpleChanges, ViewChild, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Price } from 'src/app/models/types/Product/Price.type';
import { Color, HighJewelryStockInfo, ProductDetailPageDTO, ProductParureDTO, Products, Size, Sizes } from 'src/app/models/types/Product/Product.type';
import { StockAndPriceService } from 'src/app/services/api/stock-and-price.service';
import { PriceListModalComponent } from '../../modals/price-list-modal/price-list-modal.component';
import { PriceListDTO } from 'src/app/models/types/Product/PriceListDTO.type';
import { ToastService } from 'src/app/services/toast.service';
import { SharedService } from 'src/app/services/shared.service';
import { MultiRowTextEllipsisComponent } from '../../multi-row-text-ellipsis/multi-row-text-ellipsis.component';
import { LoaderService } from 'src/app/services/loader.service';
import { Collapse } from 'bootstrap';
import { SizeListModalComponent } from '../../modals/size-list-modal/size-list-modal.component';
import { ColorListModalComponent } from '../../modals/color-list-modal/color-list-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ProductComponent } from '../../product/product.component';
import { SharedModule } from 'src/app/modules/shared.module';

@Component({
  selector: 'app-detail',
  imports: [
    TranslateModule,
    PriceListModalComponent,
    SizeListModalComponent,
    ColorListModalComponent,
    MultiRowTextEllipsisComponent,
    SharedModule
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss'
})
export class DetailComponent implements OnInit, AfterViewInit, OnChanges {

  @ViewChild('moreDetailsAccordion') moreDetailsAccordion!: ElementRef;
  @ViewChild('priceListModal', { static: false }) priceListModal!: PriceListModalComponent;
  @ViewChild('colorListModal', { static: false }) colorListModal!: ColorListModalComponent;
  @ViewChild('sizeListModal', { static: false }) sizeListModal!: SizeListModalComponent;

  @Input() pdp!: ProductDetailPageDTO | null;
  @Input() parure!: Products;
  @Input() price!: Price | null | undefined;
  @Input() highJewelryStockInfo?: HighJewelryStockInfo | null;
  @Input() showStockLoader!: Signal<boolean>;

  @Output() loadPDP: EventEmitter<string> = new EventEmitter();
  @Output() viewStock: EventEmitter<void> = new EventEmitter();

  public collapseInstance!: Collapse;
  public isMoreDetailsAccordionCollapsed: WritableSignal<boolean> = signal(true);

  private _priceList: WritableSignal<PriceListDTO | null> = signal(null);
  public priceList: Signal<PriceListDTO | null> = computed(() => this._priceList());

  private _selectedSize: WritableSignal<Size | null> = signal(null);
  public selectedSize: Signal<Size | null> = computed(() => this._selectedSize());

  private _selectedColor: WritableSignal<Color | null> = signal(null);
  public selectedColor: Signal<Color | null> = computed(() => this._selectedColor());

  public get isHighJewelry(): boolean {
    return this.pdp?.category === "HIGH_JEWELRY";
  }

  public get canShowSizeList(): boolean {
    return Boolean(this.pdp && this.pdp.sizes && this.pdp.sizes?.length > 0 && !this.pdp.isBatchManaged);
  }

  public get canShowColorList(): boolean {
    return Boolean(this.pdp && this.pdp.colors && this.pdp.colors?.length > 0);
  }

  public get canShowViewStock(): boolean {
    return !this.authService.user?.isUserNoStore && !this.authService.user?.isUserBp;
  }

  public get includingTaxes(): boolean {
    return !!this.price?.tax;
  }

  public get priceLabel(): string {
    return this.sharedService.getPriceLabel(this.price);
  }

  public get canShowPrice(): boolean {
    return (!!this.authService.user?.isUserStore || !!this.authService.user?.isUserBp) && !this.isHighJewelry;
  }

  public get canShowPriceListButton(): boolean {
    return !(!!this.authService.user?.isUserBp && !!this.pdp?.isBatchManaged);
  }

  public get shouldShowLoaderViewStockButton(): boolean {
    return !this.authService.user?.isUserGenerallyHQ;
  }

  constructor(
    private readonly toastService: ToastService,
    private readonly sharedService: SharedService,
    private readonly loaderService: LoaderService,
    private readonly authService: AuthService,
    private readonly stockAndPriceService: StockAndPriceService,
  ) { }

  ngOnInit(): void {
    this.loadSelectedSize();
    this.loadSelectedColor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["pdp"]?.firstChange) {
      this.loadSelectedSize();
    }
  }

  ngAfterViewInit(): void {
    this.collapseInstance = new Collapse(this.moreDetailsAccordion.nativeElement, { toggle: false });
    //Di default occorre far apparire l'accordion già aperto
    this.toggle();
  }

  loadSelectedSize() {
    if (this.pdp && this.pdp.sizes) {
      let size = this.pdp.sizes.find(s => s.sapCode === this.pdp?.sapCode);
      this._selectedSize.set(size ?? null);
    }
  }

  loadSelectedColor() {
    if (this.pdp && this.pdp.colors) {
      let color = this.pdp.colors.find(s => s.sapCode === this.pdp?.sapCode);
      this._selectedColor.set(color ?? null);
    }
  }

  loadPriceList(callback?: Function) {
    this.loaderService.show();
    this.stockAndPriceService.getPriceListBySapCode(this.pdp!.sapCode).subscribe({
      next: (res) => {
        this._priceList.set(res);
        this.loaderService.hide();
        callback && callback();
      },
      error: (err) => {
        this._priceList.set([]);
        this.toastService.showError("Unable to load Price List");
        this.loaderService.hide();
        console.log(err);
      }
    })
  }

  showPriceList() {
    let priceList = this.priceList();
    if (priceList === null) {
      this.loadPriceList(() => this.showPriceList());
    } else {
      this.priceListModal.openModal(priceList);
    }
  }

  showSizeList() {
    if (this.canShowSizeList) {
      this.sizeListModal.openModal(this.pdp!.sizes!, this.selectedSize());
    }
  }

  showColorList() {
    if (this.canShowColorList) {
      this.colorListModal.openModal(this.pdp!.colors || []);
    }
  }

  toggle() {
    this.collapseInstance.toggle();
    this.isMoreDetailsAccordionCollapsed.set(!this.isMoreDetailsAccordionCollapsed());
  }

  handleSelectSize(size: Size) {
    this.loadPDP.emit(size.sapCode);
  }

  handleSelectColor(color: Color) {
    this.loadPDP.emit(color.sapCode);
  }

  handleProductClick(sapCode: any): void {
    this.sharedService.navigateTo_ProductPage(sapCode, true);
  }

  getYesOrNoText(value: any) {
    switch (typeof value) {
      case "string": {
        return value.toLowerCase() === "true" ? "Yes" : "No";
      }
      case "boolean": {
        return value ? "Yes" : "No"
      }
      default: return "-"
    }
  }

}
